import { DeleteOutlined, EditOutlined, PlusOutlined, SyncOutlined } from "@ant-design/icons";
import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Image,
  Input,
  Layout,
  Menu,
  notification,
  PageHeader,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useApplicationContext } from "components/Application";
import React, { Fragment, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

const articleItemsQuery = gql`
  query ($pagination: ConnectionPagination, $sorting: ArticleItemSorting, $searchQuery: String) {
    articleItems(pagination: $pagination, sorting: $sorting, searchQuery: $searchQuery) {
      nodes {
        id
        code
        nameEn
        cover
        available
        orderable
        brand {
          id
          nameEn
        }
        externalComposition {
          id
          code
        }
        season {
          id
          code
          nameEn
          kindEn
          isEnabled
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const magoSyncItemsMutation = gql`
  mutation {
    magoSyncItems {
      successful
    }
  }
`;

const deleteArticleItemMutation = gql`
  mutation ($id: ID!) {
    deleteArticleItem(id: $id) {
      item {
        id
        code
      }
    }
  }
`;

export const List: React.FC<{}> = () => {
  const navigate = useNavigate();
  const {
    siteEnvironment: { appEndpointStore },
  } = useApplicationContext();
  const [searchQuery, setSearchQuery] = useState<string>("");

  const variables = {
    pagination: { page: 1, pageSize: 0 },
    orderBy: { direction: "ASC", field: "ID" },
    searchQuery,
  };
  const { loading, data, refetch } = useQuery(articleItemsQuery, { variables, fetchPolicy: "cache-and-network" });

  const handleSearch = (value: string) => setSearchQuery(value);
  const handleSearchCancel = () => setSearchQuery("");
  const handleTableChange = () => {};

  const [magoSyncItems, { loading: syncLoading }] = useMutation(magoSyncItemsMutation, {});
  const [syncEnabled, setSyncEnabled] = useState(true);

  const [execDelete] = useMutation(deleteArticleItemMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      notification.error({
        placement: "bottomRight",
        message: "Cannot delete this Item",
      });
    },
    onCompleted: ({ deleteArticleItem: { item } }) => {
      notification.success({
        placement: "bottomRight",
        message: `Item "${item.code}" deleted`,
      });
      refetch();
    },
  });

  const columns = [
    {
      title: "Orderable",
      dataIndex: "orderable",
      render: (value: boolean) =>
        value ? (
          <Tag color="green">√</Tag>
        ) : (
          <Tag color="red">X</Tag>
        ),
        sorter: (a: any, b: any) => a.orderable - b.orderable,
        filters: [
          {
            text: 'Orderable',
            value: true,
          },
          {
            text: 'Not Orderable',
            value: false,
          },
        ],
        onFilter: (value: boolean, record: any) => record.orderable.toString().indexOf(value) === 0,
    },
    {
      title: "Cover",
      dataIndex: "cover",
      render: (value: string) => <Image src={`${appEndpointStore}${value}`} width={50} />,
    },
    {
      title: "Code",
      dataIndex: "code",
      render: (value: string, resource: any) =>
        resource.available ? (
          <>
            <NavLink to={resource.id}>{value}</NavLink>
          </>
        ) : (
          <>
            <NavLink to={resource.id}><Typography.Text type="danger">{value}</Typography.Text></NavLink>
          </>
        ),
      sorter: (a: any, b: any) => a.code.localeCompare(b.code),
    },
    {
      title: "Name",
      dataIndex: "nameEn",
      sorter: (a: any, b: any) => a.nameEn.localeCompare(b.nameEn),
    },
    {
      title: "Ext.Comp.",
      render: (resource: any) => <NavLink to={`/global/attributes/compositions/${resource.externalComposition.id}/edit`}>{resource.externalComposition.code}</NavLink>,
      sorter: (a: any, b: any) => a.externalComposition.code.localeCompare(b.externalComposition.code),
    },
    {
      title: "Brand",
      render: (resource: any) => <Tag>{resource.brand.nameEn.toUpperCase()}</Tag>,
      sorter: (a: any, b: any) => a.brand.nameEn.localeCompare(b.brand.nameEn),
      filters: [
        {
          text: 'PALTÒ',
          value: "Paltò",
        },
        {
          text: 'REPLUMÉ',
          value: "Replumé",
        },
      ],
      onFilter: (value: any, record: any) => record.brand.nameEn.toString().indexOf(value) === 0,
    },
    {
      title: "Season",
      render: (resource: any) =>
        resource.season.isEnabled ? (
          <Tag color="blue">{resource.season.nameEn} - {resource.season.kindEn}</Tag>
        ) : (
          <Tag color="red">{resource.season.nameEn} - {resource.season.kindEn}</Tag>
        ),
        sorter: (a: any, b: any) => a.season.nameEn.localeCompare(b.season.nameEn),
        // filters: [
        //   {
        //     text: 'Paltò FW22 - Man',
        //     value: "ai22pu",
        //   },
        //   {
        //     text: 'Paltò FW22 - Woman',
        //     value: "ai22pd",
        //   },
        //   {
        //     text: 'Paltò FW22 - Unisex',
        //     value: "ai22px",
        //   },
        //   {
        //     text: 'Replumè FW22 - Man',
        //     value: "ai22ru",
        //   },
        //   {
        //     text: 'Replumè FW22 - Woman',
        //     value: "ai22rd",
        //   },
        //   {
        //     text: 'Replumè FW22 - Unisex',
        //     value: "ai22rx",
        //   },
        // ],
        // onFilter: (value: any, record: any) => record.season.code.indexOf(value) === 0,
    },
    {
      width: "80px",
      title: "Actions",
      dataIndex: "id",
      render: (id: string, resource: any) => (
        <Row justify="end">
          <Space>
            <Tooltip title="Edit" placement="bottom">
              <Button onClick={() => navigate(`${id}/edit`)} icon={<EditOutlined />}></Button>
            </Tooltip>
            <Popconfirm title="Are you sure?" icon={null} onConfirm={() => execDelete({ variables: { id } })}>
              <Tooltip title="Delete" placement="bottom">
                <Button danger icon={<DeleteOutlined />}></Button>
              </Tooltip>
            </Popconfirm>
          </Space>
        </Row>
      ),
    },
  ];

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Input.Search
              placeholder="search"
              onSearch={handleSearch}
              key={variables.searchQuery ?? "__search__"}
              defaultValue={variables.searchQuery ?? ""}
              style={{ width: 500, margin: "16px 30px" }}
            />
            <Button disabled={searchQuery.length === 0} onClick={handleSearchCancel} style={{ margin: "16px 0" }}>
              Cancel
            </Button>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <PageHeader
          ghost={true}
          title="Items"
          extra={
            <Space size="large">
              <Popconfirm
                disabled={!syncEnabled}
                title="Are you sure?"
                icon={null}
                onConfirm={() => {
                  magoSyncItems();
                  setSyncEnabled(false);
                }}
              >
                <Button loading={syncLoading} disabled={!syncEnabled}>
                  <SyncOutlined />
                  Mago Sync
                </Button>
              </Popconfirm>
              <Button onClick={() => navigate("new")} type="primary">
                <PlusOutlined />
                New
              </Button>
            </Space>
          }
        />
        <Layout.Content className="content">
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={data?.articleItems.nodes}
            pagination={{
              hideOnSinglePage: true,
              showSizeChanger: true,
              showTotal: (total, range) => `Showing ${range} of ${total}`,
              total: data?.totalCount ?? 0,
            }}
            onChange={handleTableChange}
          />
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
